import { Link } from "react-router-dom";
import { Percent, Zap, Package, ShieldCheck, type LucideIcon } from "lucide-react";

interface Pillar {
  icon: LucideIcon;
  stat: string;
  label: string;
  description: string;
  badge?: string;
}

const pillars: Pillar[] = [
  {
    icon: Percent,
    stat: "Discounted",
    label: "Reduced commission",
    description:
      "Founding sellers lock in a discounted commission rate while we grow together. Standard rates apply later.",
    badge: "Founding offer",
  },
  {
    icon: Zap,
    stat: "< 48 hrs",
    label: "Live in under 48 hours",
    description:
      "Apply, get approved, and start selling — no lengthy review cycles or technical setup.",
  },
  {
    icon: Package,
    stat: "Your way",
    label: "Your fulfillment or ours",
    description:
      "Ship yourself, or use Fulfill by HJB — we store, pack, and ship from our US warehouse. No setup fees.",
  },
  {
    icon: ShieldCheck,
    stat: "Verified",
    label: "Curated sellers only",
    description:
      "Every brand is reviewed before listing. Your products appear alongside a trusted, curated selection.",
  },
];

// Dark surface, same treatment as the footer: `fabric-texture-dark` supplies the
// ground and veil, and the text palette below mirrors the footer's
// white / white-60 / white-10 scale so the two dark bands read as one family.
const VendorSocialProofStrip = () => (
  <section className="w-full fabric-texture fabric-texture-dark text-[hsl(40_10%_85%)]">
    <div className="mx-auto px-4 py-8 md:px-11 md:pt-9 md:pb-12">
      {/* ZONE 1 — Headline + CTA */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5 md:gap-6 mb-6 md:mb-7">
        {/* Left */}
        <div>
          {/* Sizing stays inline: `t-mono` pins 11px/400 from a CSS layer, and
              inline style is the one place that reliably wins over it. */}
          <p
            className="uppercase mb-3 t-mono"
            style={{
              fontSize: "14px",
              fontWeight: 600,
              letterSpacing: "0.16em",
              color: "hsl(var(--coral))",
              lineHeight: 1.4,
            }}
          >
            For brands &amp; designers
          </p>
          <h2
            className="font-serif max-w-lg text-white text-[22px] md:text-[32px] leading-[1.1] tracking-tight font-medium"
          >
            Sell to customers who are already looking for you.
          </h2>
        </div>

        {/* Right */}
        <div className="flex items-center gap-4 shrink-0">
          <Link
            to="/vendor/register"
            className="hover:opacity-90 transition-opacity"
            style={{
              fontSize: "13px",
              fontWeight: 500,
              padding: "11px 22px",
              borderRadius: "8px",
              backgroundColor: "hsl(var(--coral))",
              color: "hsl(var(--cream))",
              whiteSpace: "nowrap",
            }}
          >
            Become a founding seller
          </Link>
          <p
            className="text-white/60"
            style={{
              fontSize: "11px",
              lineHeight: 1.4,
              maxWidth: "140px",
            }}
          >
            Discounted commission for founding sellers. Lock your rate before standard pricing.
          </p>
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-white/10" />

      {/* ZONE 2 — Four pillars */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 md:gap-0 mt-7">
        {pillars.map((p, i) => {
          const Icon = p.icon;
          const isLast = i === pillars.length - 1;
          return (
            <div
              key={i}
              className={`text-left px-0 md:px-7 ${!isLast ? "md:border-r md:[border-right-width:0.5px] border-white/10" : ""}`}
            >
              {/* Icon */}
              <div
                className="flex items-center justify-center mb-3"
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  /* Rose at 0.55 was a light tile for a cream page; on the dark
                     ground it needs to be a faint warm wash so the coral icon
                     stays the brightest thing in the tile. */
                  backgroundColor: "hsl(var(--rose) / 0.12)",
                }}
              >
                <Icon size={18} color="hsl(var(--coral))" strokeWidth={1.75} />
              </div>

              {/* Stat */}
              <p
                className="font-serif text-white"
                style={{
                  fontSize: "26px",
                  fontWeight: 500,
                  lineHeight: 1.15,
                  letterSpacing: "-0.01em",
                }}
              >
                {p.stat}
              </p>

              {/* Label */}
              <p
                className="mt-1 text-white"
                style={{
                  fontSize: "13px",
                  fontWeight: 500,
                  lineHeight: 1.4,
                }}
              >
                {p.label}
              </p>

              {/* Description */}
              <p
                className="mt-1.5 text-white/60"
                style={{
                  fontSize: "12px",
                  lineHeight: 1.55,
                }}
              >
                {p.description}
              </p>

              {/* Badge */}
              {p.badge && (
                <span
                  className="inline-block mt-3"
                  style={{
                    fontSize: "11px",
                    fontWeight: 500,
                    color: "hsl(var(--charcoal))",
                    /* Raised from 0.30: the chip keeps its dark charcoal text,
                       so on the dark ground the sage has to stay opaque enough
                       to read as a light pill rather than dark-on-dark. */
                    backgroundColor: "hsl(var(--sage) / 0.85)",
                    padding: "3px 10px",
                    borderRadius: "999px",
                    lineHeight: 1.4,
                  }}
                >
                  {p.badge}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

export default VendorSocialProofStrip;
